import { Component, Output, EventEmitter, Inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PrimeNGSharedModule } from '@shared/prime/prime-ng.module';

@Component({
  selector: 'app-certificate-cell',
  standalone: true,
  imports: [CommonModule, PrimeNGSharedModule],
  template: `
    <div class="certificate-cell-wrapper">
      <ng-container [ngSwitch]="displayState">
        <button
          *ngSwitchCase="'passed'"
          type="button"
          class="icon-btn"
          (click)="onViewCertificate()"
          [pTooltip]="tooltip"
          tooltipPosition="top"
          aria-label="View certificate"
        >
          <img src="assets/icons/file.png" alt="Certificate" class="file-icon" />
        </button>

        <span
          *ngSwitchCase="'scanning'"
          class="status-icon scanning"
          [pTooltip]="tooltip"
          tooltipPosition="top"
          aria-label="Certificate scan in progress"
        >
          <i class="pi pi-clock"></i>
        </span>

        <span
          *ngSwitchCase="'failed'"
          class="status-icon failed"
          [pTooltip]="tooltip"
          tooltipPosition="top"
          aria-label="Certificate scan failed"
        >
          <i class="pi pi-exclamation-triangle"></i>
        </span>

        <span *ngSwitchDefault class="status-text">N/A</span>
      </ng-container>
    </div>
  `,
  styles: [`
    .certificate-cell-wrapper {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      height: 100%;
      padding-left: 10px;
    }

    .icon-btn {
      border: none;
      background: none;
      padding: 0;
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    .icon-btn:focus {
      outline: 2px solid var(--primary-color, #4e46b4);
      outline-offset: 2px;
    }

    .file-icon {
      width: 24px;
      height: 24px;
    }

    .status-icon {
      display: flex;
      align-items: center;
      font-size: 1.2rem;
    }

    .status-icon.scanning {
      color: var(--primary-color, #ca8a04);
      margin-left: 4px;
    }

    .status-icon.failed {
      color: var(--error-color, #d14343);
      margin-left: 4px;
    }

    .status-text {
      color: var(--text-color-secondary, #6c757d);
      font-size: 0.875rem;
    }
  `]
})
export class CertificateCellComponent {
  private readonly certificateInfo: any;

  // Inject the certificateUrl provided by the parent injector
  constructor(
    @Optional() @Inject('certificateUrl') private readonly injectedCertificateUrl: string | null,
    @Optional() @Inject('certificateInfo') certificateInfo: any,
    @Optional() @Inject('viewCertificateCallback') private readonly viewCertificateCallback?: (event: { url: string; fileName: string }) => void
  ) {
    this.certificateInfo = certificateInfo ?? null;
    this.certificateUrl = this.certificateInfo?.url ?? this.injectedCertificateUrl ?? null;
  }

  certificateUrl: string | null = null;

  @Output() viewCertificate = new EventEmitter<{ url: string, fileName: string }>();

  get displayState(): 'passed' | 'scanning' | 'failed' | 'missing' {
    const status = (this.certificateInfo?.status || '').toLowerCase();

    if (status === 'scanning') {
      return 'scanning';
    }

    if (status === 'failed') {
      return 'failed';
    }

    if (status === 'passed') {
      return this.isValidLink(this.certificateUrl || '') ? 'passed' : 'missing';
    }

    return this.isValidLink(this.certificateUrl || '') ? 'passed' : 'missing';
  }

  get tooltip(): string {
    const defaultMessages: Record<string, string> = {
      passed: 'View certificate',
      scanning: 'Security scan is in progress.',
      failed: 'Security scan failed. Please upload a new certificate.',
      missing: 'Certificate not available.',
    };

    return this.certificateInfo?.message || defaultMessages[this.displayState];
  }

  private extractFileName(): string {
    if (this.certificateInfo?.file_name) {
      return this.certificateInfo.file_name;
    }

    const url = this.certificateUrl || '';
    if (!url) {
      return 'certificate.pdf';
    }
    return url.substring(url.lastIndexOf('/') + 1).split('?')[0] || 'certificate.pdf';
  }

  // Emit the `certificateUrl` as event if it's valid
  onViewCertificate(): void {
    if (!this.certificateUrl || this.displayState !== 'passed') {
      return;
    }

    if (this.isValidLink(this.certificateUrl)) {
      const payload = { url: this.certificateUrl, fileName: this.extractFileName() };

      if (this.viewCertificateCallback) {
        this.viewCertificateCallback(payload);
      } else {
        this.viewCertificate.emit(payload);
      }
    }
  }

  /**
   * A simple check for any http(s) URL, including attachments or firebase links.
   */
  isValidLink(url: string): boolean {
    return typeof url === 'string' && /^(https?:)\/\//.test(url) && !url.trim().endsWith('/');
  }
}